import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertCircle, Save } from 'lucide-react';
import TimePicker12h from './TimePicker12h';
import { useQuery } from '@tanstack/react-query';
import { useCallStore } from '../stores/callStore';
import axiosInstance from '../api/axiosInstance';
import API_ENDPOINTS from '../api/endpoints';
import toast from 'react-hot-toast';
import { leadFlowLog } from '../utils/leadFlowLogger';
import { gaDispositionSaved } from '../utils/analytics';
import { readDispositionsCache, writeDispositionsCache } from '../utils/dispositionsCache';
import { maskPhone as maskPhoneFor } from '../utils/pii.js';
import styles from './DispositionModal.module.css';



const DispositionModal = ({ onDispositionSaved }) => {
  const { user } = useSelector((state) => state.auth);
  const maskPhone = (num) => maskPhoneFor(num, user);
  const { currentLead, dispositionRequired, completeDisposition } = useCallStore();
  const [mainDisposition, setMainDisposition] = useState('');
  const [subDisposition, setSubDisposition] = useState('');
  const [notes, setNotes] = useState('');
  const [callNextDate, setCallNextDate] = useState('');
  const [callNextTime, setCallNextTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isOpen = dispositionRequired && currentLead;

  // Log when disposition modal opens
  useEffect(() => {
    if (isOpen) leadFlowLog.dispositionOpened(currentLead);
  }, [isOpen, currentLead]);

  // Dispositions are static org config, so we serve them from the browser cache
  // (primed at login, cleared at logout) and only hit the network when the cache
  // is cold or has gone stale — not on every modal open. Keyed by org so a
  // superadmin switching organisations gets the right list.
  const cacheOrg = user?.userOrgId;
  const { data: dispositionsData, isLoading: loadingDispositions } = useQuery({
    queryKey: ['dispositionsByOrg', cacheOrg || 'self'],
    queryFn: async () => {
      const response = await axiosInstance.get(API_ENDPOINTS.getDispositionsByOrg);
      const res = response.data?.response?.[0];
      return res?.data || [];
    },
    enabled: isOpen && !!user?.userCode,
    initialData: () => readDispositionsCache(cacheOrg),
    staleTime: 30 * 60 * 1000, // treat as fresh for 30 min; self-heals after
    gcTime: 60 * 60 * 1000,
  });

  // Persist fresh, non-empty results so the next session/open is instant.
  useEffect(() => {
    if (Array.isArray(dispositionsData) && dispositionsData.length) {
      writeDispositionsCache(cacheOrg, dispositionsData);
    }
  }, [dispositionsData, cacheOrg]);

  const mainDispositions = dispositionsData || [];
  const selectedMain = mainDispositions.find((d) => d.mainDispositionName === mainDisposition);
  const subDispositions = selectedMain?.subDispositions?.filter((s) => s.active !== false) || [];
  const selectedSub = subDispositions.find((s) => s.subDispositionName === subDisposition);
  const showCallNextTime = selectedSub?.showCallNextTime === true;

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      // Prevent ESC key from closing modal
      const handleEsc = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      document.addEventListener('keydown', handleEsc);

      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEsc);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  // Reset sub-disposition and call next time when main changes
  useEffect(() => {
    if (mainDisposition) {
      setSubDisposition('');
      setCallNextDate('');
      setCallNextTime('');
      setError(null);
    }
  }, [mainDisposition]);

  // Reset call next time when sub-disposition changes
  useEffect(() => {
    if (subDisposition) {
      const selected = subDispositions.find((s) => s.subDispositionName === subDisposition);
      if (selected?.showCallNextTime !== true) {
        setCallNextDate('');
        setCallNextTime('');
      }
      setError(null);
    } else {
      setCallNextDate('');
      setCallNextTime('');
    }
  }, [subDisposition, subDispositions]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all fields when modal closes
      setMainDisposition('');
      setSubDisposition('');
      setCallNextDate('');
      setCallNextTime('');
      setNotes('');
      setError(null);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!mainDisposition.trim()) {
      setError('Please select a main disposition');
      return;
    }
    if (!subDisposition.trim()) {
      setError('Please select a sub-disposition');
      return;
    }
    if (showCallNextTime && !callNextDate.trim()) {
      setError('Please select a call next date');
      return;
    }
    if (showCallNextTime && callNextDate.trim() && !callNextTime.trim()) {
      setError('Please select a call next time');
      return;
    }

    if (showCallNextTime && callNextDate.trim() && callNextTime.trim()) {
      const selectedDateTime = new Date(`${callNextDate}T${callNextTime}`);
      if (selectedDateTime < new Date()) {
        setError('Follow-up date & time cannot be in the past');
        return;
      }
    }

    setSaving(true);
    setError(null);
    leadFlowLog.dispositionSaveStart(currentLead);

    try {
      const leadId = currentLead.leadID || currentLead.id;
      const leadUserId = user?._id || user?.userId;
      if (!leadUserId) {
        setError('User session missing. Please log in again.');
        setSaving(false);
        return;
      }

      // Use dispose/addLeadDisposition API (POST /addLeadDisposition or POST /dispose)
      // Backend expects: leadId, leadMainDispositionName, leadSubDispositionName, leadUserId
      // Optional: leadCallNextDate, leadCallNextTime (when showCallNextTime is true)
      const payload = {
        leadId,
        leadMainDispositionName: mainDisposition,
        leadSubDispositionName: subDisposition,
        leadUserId,
        ...(notes.trim() && { notes: notes.trim() }),
        ...(showCallNextTime && callNextDate.trim() && { leadCallNextDate: callNextDate }),
        ...(showCallNextTime && callNextTime.trim() && { leadCallNextTime: callNextTime }),
      };

      const response = await axiosInstance.post(
        API_ENDPOINTS.addLeadDisposition,
        payload
      );

      const res = response.data?.response?.[0];
      const success = res?.status === 1 || res?.status === true;

      if (success) {
        toast.success('Disposition saved successfully');
        gaDispositionSaved(leadId, user?.userCode, mainDisposition, subDisposition);
        leadFlowLog.dispositionSaveSuccess(currentLead, [
          'completeDisposition() → needLead=true → fetchNextLead()',
        ]);
        completeDisposition();
        if (onDispositionSaved) onDispositionSaved();
      } else {
        throw new Error(res?.message || 'Failed to save disposition');
      }
    } catch (err) {
      leadFlowLog.dispositionSaveError(err);
      console.error('Error saving disposition:', err);
      const msg = err.response?.data?.response?.[0]?.message || err.message || 'Failed to save disposition. Please try again.';
      setError(msg);
      toast.error(err.response?.data?.response?.[0]?.message || 'Failed to save disposition');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <div className={styles.modalOverlay}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={styles.overlayBackdrop}
          onClick={(e) => {
            // Prevent closing on backdrop click
            e.preventDefault();
            e.stopPropagation();
          }}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className={styles.modalContent}
        >
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>Call Disposition</h2>
            <p className={styles.modalSubtitle}>
              Please provide disposition details for this call
            </p>
          </div>

          <div className={styles.modalBody}>
            {/* Lead Info */}
            {currentLead && (
              <div className={styles.leadInfo}>
                <div className={styles.leadName}>{currentLead.leadName || 'Unknown Lead'}</div>
                <div className={styles.leadPhone}>
                  {maskPhone(currentLead.leadMobileNumber || currentLead.phone)}
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className={styles.errorMessage}>
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Main Disposition */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Main Disposition <span className={styles.required}>*</span>
              </label>
              {loadingDispositions ? (
                <div className={styles.loadingSelect}>
                  <Loader2 size={16} className={styles.spinner} />
                  <span>Loading dispositions...</span>
                </div>
              ) : (
                <select
                  value={mainDisposition}
                  onChange={(e) => {
                    setMainDisposition(e.target.value);
                    setSubDisposition('');
                    setError(null);
                  }}
                  className={styles.select}
                  disabled={saving}
                >
                  <option value="">Select main disposition...</option>
                  {mainDispositions.map((d) => (
                    <option key={d._id || d.mainDispositionName} value={d.mainDispositionName}>
                      {d.mainDispositionName}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Sub Disposition */}
            {mainDisposition && (
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Sub Disposition <span className={styles.required}>*</span>
                </label>
                {subDispositions.length === 0 ? (
                  <div className={styles.noSubDispositions}>
                    No sub-dispositions available for this main disposition.
                    <input
                      type="text"
                      value={subDisposition}
                      onChange={(e) => {
                        setSubDisposition(e.target.value);
                        setError(null);
                      }}
                      className={styles.input}
                      placeholder="Enter sub-disposition..."
                      disabled={saving}
                    />
                  </div>
                ) : (
                  <select
                    value={subDisposition}
                    onChange={(e) => {
                      setSubDisposition(e.target.value);
                      setError(null);
                    }}
                    className={styles.select}
                    disabled={saving}
                  >
                    <option value="">Select sub-disposition...</option>
                    {subDispositions.map((s) => (
                      <option key={s._id || s.subDispositionName} value={s.subDispositionName}>
                        {s.subDispositionName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Call Next Date/Time - Show when showCallNextTime is true */}
            {showCallNextTime && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Call Next Date <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="date"
                    value={callNextDate}
                    onChange={(e) => {
                      setCallNextDate(e.target.value);
                      setCallNextTime('');
                      setError(null);
                    }}
                    className={styles.input}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={saving}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Call Next Time <span className={styles.required}>*</span>
                  </label>
                  <TimePicker12h
                    value={callNextTime}
                    onChange={(val) => {
                      setCallNextTime(val);
                      setError(null);
                    }}
                    disabled={saving}
                    minDate={callNextDate}
                  />
                </div>
              </>
            )}

            {/* Notes */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Notes (Optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={styles.textarea}
                placeholder="Add any additional notes about this call..."
                rows={4}
                disabled={saving}
              />
            </div>

            {/* Warning */}
            <div className={styles.warningMessage}>
              <AlertCircle size={16} />
              <span>You must save the disposition before proceeding to the next lead.</span>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button
              onClick={handleSave}
              disabled={
                saving ||
                !mainDisposition.trim() ||
                !subDisposition.trim() ||
                (showCallNextTime && (!callNextDate.trim() || !callNextTime.trim()))
              }
              className={styles.saveButton}
            >
              {saving ? (
                <>
                  <Loader2 size={18} className={styles.spinner} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Disposition
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default DispositionModal;
